import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { JsonRpcProvider } from 'ethers';
import { createPublicClient, http } from 'viem';
import { mainnet } from 'viem/chains';
import Zwallet from '../components/Zwallet';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Enhanced ENS resolution with fallbacks
const checkENS = async (address) => {
  if (!address || !address.startsWith('0x')) return null;
  
  console.log(`🔍 Checking ENS for: ${address}`);
  
  const publicRPCs = [
  'https://rpc.ankr.com/eth/dec8e5317af4d9c8cb41cc5bfaef26700319860751215bc62794cd3cafdd691d', // ✅ Your key
  'https://ethereum.publicnode.com',  // ✅ Backup 1
  'https://cloudflare-eth.com'        // ✅ Backup 2
  ];
  
  for (const rpcUrl of publicRPCs) {
    try {
      console.log(`🔄 Trying ENS lookup via ${rpcUrl}`);
      const provider = new JsonRpcProvider(rpcUrl);
      const name = await provider.lookupAddress(address);
      
      if (name) {
        console.log(`✅ ENS found: ${name} (via ${rpcUrl})`);
        return name;
      }
      
      console.log(`ℹ️ No ENS name for ${address} (via ${rpcUrl})`);
      return null;
      
    } catch (error) {
      console.warn(`⚠️ ENS lookup failed on ${rpcUrl}:`, error.message);
    }
  }
  
  console.log('❌ All ENS lookups failed');
  return null;
};

// Helper to get auth headers for API calls
const getAuthHeaders = () => {
  const token = localStorage.getItem('zhat_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const AuthProvider = ({ children, chain = mainnet }) => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [ensName, setEnsName] = useState(null);
  const [address, setAddress] = useState(null);
  const [authStatus, setAuthStatus] = useState('idle');
  const [integrationStatus, setIntegrationStatus] = useState('ready');
  
  // Zwallet modal state
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [zwalletConnectionStatus, setZwalletConnectionStatus] = useState('idle');
  const [zwalletMode, setZwalletMode] = useState('connect');
  const [hasZwalletCreated, setHasZwalletCreated] = useState(false);
  
  // Wallet state from Zwallet
  const [zwalletAddress, setZwalletAddress] = useState(null);
  const [zwalletProvider, setZwalletProvider] = useState(null);
  const [zwalletSigner, setZwalletSigner] = useState(null);
  const [zwalletIsConnected, setZwalletIsConnected] = useState(false);
  
  // Additional state
  const [skipConnectionScreen, setSkipConnectionScreen] = useState(false);
  const [isWalletOnlyConnected, setIsWalletOnlyConnected] = useState(false);
  const [zwalletInstance, setZwalletInstance] = useState(null);
  const [walletTransactionFunctions, setWalletTransactionFunctions] = useState({
    sendTransaction: null,
    signMessage: null,
    showUnlockModal: null, // <-- ADDED
    isReady: false
  });

  const INFURA_PROJECT_ID = 'YOUR_INFURA_PROJECT_ID_HERE';
  
  const publicClient = createPublicClient({
    chain,
    transport: http(`https://mainnet.infura.io/v3/${INFURA_PROJECT_ID}`),
    batch: { multicall: true },
  });

    const checkZpostDomainOwnership = useCallback(async (walletAddress) => {
      try {
        setAuthStatus('checking-ens');
        setIntegrationStatus('ens-checking');

        console.log('🔍 Checking ENS for:', walletAddress);
        const ens = await checkENS(walletAddress);
        console.log('🔍 ENS resolved:', ens);

        if (!ens) {
          throw new Error('No ENS name found. You need a .zpost.eth domain to access Zpost.');
        }

        if (!ens.toLowerCase().endsWith('.zpost.eth')) {
          throw new Error(`Access requires .zpost.eth domain. Your current ENS: ${ens}. Visit /ens to mint one.`);
        }

        console.log('✅ Valid .zpost.eth confirmed:', ens);

        // Backend JWT login
        console.log('🚀 Logging into backend with ENS:', ens);
        const loginResponse = await fetch('https://zpost.ai/api/auth/ens-verified-login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ensName: ens,
            ethAddress: walletAddress
          })
        });

        if (!loginResponse.ok) {
          const errorData = await loginResponse.json();
          throw new Error(errorData.error || 'Backend login failed');
        }

        const { token, user } = await loginResponse.json();

        localStorage.setItem('zhat_token', token);
        console.log('✅ JWT token stored:', token.substring(0, 20) + '...');

        localStorage.setItem('zpost_ensName', ens);
        localStorage.setItem('zpost_address', walletAddress);
        localStorage.setItem('zpost_authTimestamp', Date.now().toString());

        setIsAuthenticated(true);
        setEnsName(ens);
        setAddress(walletAddress);
        setAuthStatus('authenticated');
        setIntegrationStatus('wallet-unlocked');
        setShowWalletModal(false);
        setIsWalletOnlyConnected(false);

        return { success: true, ens, address: walletAddress, token };

      } catch (err) {
        console.error('❌ ENS check failed:', err.message);
        setAuthStatus('error');
        setIntegrationStatus('ready');

        // 🔁 Redirect to public mint page if missing ENS
        if (err.message.includes('No ENS name found') && !window.location.pathname.startsWith('/mint')) {
          console.log('🔄 No ENS found – redirecting to mint page');
          window.location.href = '/mint';
          // Return a pending promise; the page will redirect
          return new Promise(() => {});
        }

        return { success: false, error: err.message };
      }
    }, []);

  const logout = useCallback(() => {
    console.log('🔐 Logging out...');
    
    // ✅ Clear Zhat token too (for shared auth)
    localStorage.removeItem('zhat_token');
    localStorage.removeItem('zpost_ensName');
    localStorage.removeItem('zpost_address');
    localStorage.removeItem('zpost_authTimestamp');
    
    setIsAuthenticated(false);
    setEnsName(null);
    setAddress(null);
    setAuthStatus('idle');
    setIntegrationStatus('ready');
    setShowWalletModal(false);
    setIsWalletOnlyConnected(false);
    
    window.dispatchEvent(new CustomEvent('zwallet:logout'));
    
    console.log('✅ Logged out (token cleared)');
  }, []);

  // Restore session on load
  useEffect(() => {
    const storedEns = localStorage.getItem('zpost_ensName');
    const storedAddress = localStorage.getItem('zpost_address');
    const storedTimestamp = localStorage.getItem('zpost_authTimestamp');
    const storedToken = localStorage.getItem('zhat_token'); // ✅ Check for token
    
    if (storedEns && storedAddress && storedTimestamp) {
      const sessionAge = Date.now() - parseInt(storedTimestamp);
      const maxSessionAge = 24 * 60 * 60 * 1000;
      
      if (sessionAge < maxSessionAge) {
        console.log('🔍 Found valid stored session, restoring...');
        setIsAuthenticated(true);
        setEnsName(storedEns);
        setAddress(storedAddress);
        setAuthStatus('authenticated');
        setIntegrationStatus('wallet-unlocked');
        
        if (!storedToken) {
          console.warn('⚠️ Session restored but no JWT token found');
        }
      } else {
        console.log('🔍 Stored session expired, clearing...');
        logout();
      }
    }
  }, [logout]);

  // Check for existing Zwallet
  useEffect(() => {
    const checkExistingZwallet = () => {
      const hasLocalWallet = localStorage.getItem('zwallet_created') === 'true';
      const hasAccounts = localStorage.getItem('zwallet_accounts') !== null;
      setHasZwalletCreated(hasLocalWallet || hasAccounts);
    };
    
    checkExistingZwallet();
  }, []);

  // Handle Zwallet connection
  const handleZwalletConnected = useCallback((walletData) => {
    console.log('✅ Zwallet connected in AuthContext:', walletData);

    setZwalletAddress(walletData.address);
    setZwalletProvider(walletData.provider);
    setZwalletSigner(walletData.signer);
    setZwalletIsConnected(true);
    setZwalletConnectionStatus('connected');
    setIntegrationStatus('wallet-unlocked');
    setAddress(walletData.address);

    if (walletData.shouldCheckENS !== false) {
      console.log('🔍 Checking ENS for full Zpost access...');
      setIntegrationStatus('ens-checking');
      checkZpostDomainOwnership(walletData.address);
    } else {
      console.log('🏦 Wallet-only connection, skipping ENS check');
      setIsWalletOnlyConnected(true);
      setAuthStatus('wallet-loaded');
    }
  }, [checkZpostDomainOwnership]);

  const handleZwalletCreated = useCallback((address) => {
    console.log('🎉 Zwallet created:', address);
    localStorage.setItem('zwallet_created', 'true');
    setHasZwalletCreated(true);
    setZwalletAddress(address);
  }, []);

  // Cross-app message handling
  useEffect(() => {
    let isMounted = true;

    const handleZwalletMessage = (event) => {
    if (window.location.pathname.startsWith('/identity') || window.location.pathname.startsWith('/mint')) {
      console.log('🛑 Ignoring wallet message on public identity/mint page');
      return;
      }

      if (!isMounted) return;
      if (event.data?.source !== 'zwallet') return;

      const data = event.data || {};
      console.log('🔗 AuthContext received Zwallet message:', data.type);

      switch (data.type) {
        case 'ZWALLET_CONNECTED':
          console.log('✅ Zwallet connected:', data.address);
          setZwalletAddress(data.address);
          setZwalletProvider(data.provider);
          setZwalletSigner(data.signer);
          setZwalletIsConnected(true);
          setZwalletConnectionStatus('connected');
          setIntegrationStatus('wallet-unlocked');

          if (data.address) {
            setAddress(data.address);
          }
          break;

        case 'ZWALLET_DISCONNECTED':
          console.log('🔌 Zwallet disconnected');
          setZwalletAddress(null);
          setZwalletProvider(null);
          setZwalletSigner(null);
          setZwalletIsConnected(false);
          setZwalletConnectionStatus('disconnected');
          setIntegrationStatus('ready');

          if (address === data.address) {
            logout();
          }
          break;

        case 'ZWALLET_READY':
          console.log('✅ Zwallet ready for connection');
          setZwalletConnectionStatus('ready');
          setIntegrationStatus('ready');
          break;

        case 'ZWALLET_CREATED':
          console.log('🎉 New Zwallet created:', data.address);
          localStorage.setItem('zwallet_created', 'true');
          setHasZwalletCreated(true);
          setZwalletAddress(data.address);
          setZwalletConnectionStatus('created');
          setIntegrationStatus('wallet-unlocked');
          break;

        case 'ZWALLET_IMPORT_REQUESTED':
          console.log('📥 Zwallet import requested via message');
          setZwalletMode('import');
          setSkipConnectionScreen(false);
          setShowWalletModal(true);
          setAuthStatus('importing-wallet');
          setZwalletConnectionStatus('importing');
          setIntegrationStatus('wallet-loading');
          break;

        default:
          console.log('📨 Unknown Zwallet message:', data.type);
      }
    };

    window.addEventListener('message', handleZwalletMessage);

    return () => {
      isMounted = false;
      window.removeEventListener('message', handleZwalletMessage);
    };
  }, [logout]);

  // Connection functions
  const connectToZwallet = useCallback(() => {
    console.log('🔗 Connecting to Zwallet...');
    setAuthStatus('connecting');
    setIntegrationStatus('wallet-loading');
    setZwalletMode('connect');
    setSkipConnectionScreen(false);
    setShowWalletModal(true);
    setZwalletConnectionStatus('connecting');
  }, []);

  const showZwalletForCreation = useCallback(() => {
    console.log('🆕 Direct wallet creation requested');
    setZwalletMode('create');
    setSkipConnectionScreen(true);
    setShowWalletModal(true);
    setAuthStatus('creating-wallet');
    setZwalletConnectionStatus('creating');
    setIntegrationStatus('wallet-loading');
  }, []);

  const showZwalletForImport = useCallback(() => {
    console.log('📥 Direct wallet import requested');
    setZwalletMode('import');
    setSkipConnectionScreen(false);
    setShowWalletModal(true);
    setAuthStatus('importing-wallet');
    setZwalletConnectionStatus('importing');
    setIntegrationStatus('wallet-loading');
  }, []);

  const connectZwalletOnly = useCallback(() => {
    console.log('🏦 Connect Zwallet Only (no ENS check)');
    setAuthStatus('connecting');
    setIntegrationStatus('wallet-loading');
    setZwalletMode('connect');
    setSkipConnectionScreen(false);
    setShowWalletModal(true);
    setZwalletConnectionStatus('connecting');
  }, []);

  const connectZpostWithENS = useCallback(() => {
    console.log('🔗 Connect Zpost with ENS check');
    setAuthStatus('connecting');
    setIntegrationStatus('wallet-loading');
    setZwalletMode('connect');
    setSkipConnectionScreen(false);
    setShowWalletModal(true);
    setZwalletConnectionStatus('connecting');
  }, []);

  const login = useCallback((options = {}) => {
    console.log('🔐 Login initiated with options:', options);
    
    const mode = options.mode || 'connect';
    const skipENSCheck = options.skipENSCheck || false;
    
    setAuthStatus('connecting');
    setIntegrationStatus('wallet-loading');
    
    if (mode === 'create') {
      return;
    }
    
    setZwalletMode(mode);
    setSkipConnectionScreen(false);
    
    if (mode === 'unlock') {
      console.log('🔓 Opening Zwallet for wallet unlock...');
      setShowWalletModal(true);
    } else if (skipENSCheck) {
      connectZwalletOnly();
    } else {
      connectZpostWithENS();
    }
  }, [connectZwalletOnly, connectZpostWithENS]);

  const exportWalletConnection = useCallback(() => {
    return {
      address: zwalletAddress,
      provider: zwalletProvider,
      signer: zwalletSigner,
      isConnected: zwalletIsConnected,
      connectionStatus: zwalletConnectionStatus
    };
  }, [zwalletAddress, zwalletProvider, zwalletSigner, zwalletIsConnected, zwalletConnectionStatus]);

  // Zwallet bridge functions
  const registerZwallet = useCallback((instance) => {
    console.log('🔗 AuthContext: Registering Zwallet instance', instance);
    setZwalletInstance(instance);
    
    if (instance) {
      // Capture all methods
      setWalletTransactionFunctions({
        sendTransaction: instance.sendTransaction,
        signMessage: instance.signMessage,
        showUnlockModal: instance.showUnlockModal, // <-- ADDED
        isReady: true
      });
      
      window.__zwallet__ = instance;
    } else {
      setWalletTransactionFunctions({
        sendTransaction: null,
        signMessage: null,
        showUnlockModal: null,
        isReady: false
      });
      
      if (window.__zwallet__) {
        delete window.__zwallet__;
      }
    }
  }, []);

  const sendTransaction = useCallback(async (transactionData) => {
    if (!walletTransactionFunctions.sendTransaction) {
      throw new Error('Zwallet not ready. Please unlock your wallet first.');
    }
    
    try {
      return await walletTransactionFunctions.sendTransaction(transactionData);
    } catch (error) {
      console.error('AuthContext: Transaction failed:', error);
      throw error;
    }
  }, [walletTransactionFunctions]);

  // Show unlock modal helper
  const showUnlockModal = useCallback(() => {
    if (walletTransactionFunctions.showUnlockModal) {
      walletTransactionFunctions.showUnlockModal();
    } else {
      // Fallback: open the wallet modal in connect mode
      console.log('No showUnlockModal method, using fallback');
      setZwalletMode('connect');
      setShowWalletModal(true);
    }
  }, [walletTransactionFunctions]);

  // Export getAuthHeaders for services
  const getAuthHeadersForServices = useCallback(() => {
    return getAuthHeaders();
  }, []);

  const contextValue = {
    isAuthenticated,
    ensName,
    address,
    authStatus,
    integrationStatus,
    
    showWalletModal,
    setShowWalletModal,
    zwalletConnectionStatus,
    zwalletMode,
    setZwalletMode,
    
    hasZwalletCreated,
    setHasZwalletCreated,
    
    wallet: exportWalletConnection(),
    walletApi: {                       // <-- ADDED: exposes methods
      sendTransaction: walletTransactionFunctions.sendTransaction,
      signMessage: walletTransactionFunctions.signMessage,
      showUnlockModal: walletTransactionFunctions.showUnlockModal,
      isReady: walletTransactionFunctions.isReady
    },
    
    login,
    logout,
    checkZpostDomainOwnership,
    connectToZwallet,
    handleZwalletCreated,
    
    isZwalletConnected: zwalletIsConnected,
    getZwalletAddress: () => zwalletAddress,
    getZwalletProvider: () => zwalletProvider,
    getZwalletSigner: () => zwalletSigner,
    
    showZwalletForCreation,
    showZwalletForImport,
    
    connectZwalletOnly,
    connectZpostWithENS,
    
    isWalletOnlyConnected,
    setIsWalletOnlyConnected,
    
    skipConnectionScreen,
    setSkipConnectionScreen,

    registerZwallet,
    sendTransaction,
    showUnlockModal,                    // <-- ADDED: convenience function
    zwalletInstance,
    walletTransactionFunctions,
    isWalletReady: walletTransactionFunctions.isReady,
    
    // Export auth headers utility
    getAuthHeaders: getAuthHeadersForServices,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      
      {showWalletModal && (
        <div 
          key={`wallet-modal-${showWalletModal}`}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
          }}
        >
          <div style={{
            width: '100%',
            maxWidth: '420px',
            height: '90vh',
            backgroundColor: 'var(--black)',
            borderRadius: '15px',
            border: '2px solid var(--gold)',
            boxShadow: '0 0 50px rgba(255, 215, 0, 0.3)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'linear-gradient(90deg, var(--black) 0%, var(--card-bg) 100%)'
            }}>
              <h2 style={{ color: 'var(--gold)', margin: 0 }}>
                {zwalletMode === 'create' ? '🏦 Create New Zwallet' : 
                 zwalletMode === 'import' ? '📥 Import Zwallet' : 
                 '🏦 Zwallet - Secure Web3 Wallet'}
              </h2>
              <button
                onClick={() => {
                  console.log('❌ Closing wallet modal');
                  setShowWalletModal(false);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--gold)',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0 10px',
                  borderRadius: '50%',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>
            
            <div style={{ 
              flex: 1, 
              overflow: 'hidden',
              position: 'relative'
            }}>
              <Zwallet 
                key="zwallet-modal-content"
                onClose={() => {
                  console.log('✅ Zwallet onClose called');
                  setShowWalletModal(false);
                }}
                onConnected={handleZwalletConnected}
                onWalletCreated={handleZwalletCreated}
                initialMode={zwalletMode}
                skipConnectionScreen={skipConnectionScreen}
              />
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
